import { doc, getDocs, setDoc } from 'firebase/firestore';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges, FieldSpecs, quoted } from '@api/ActivityLogs/DescribeChanges';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';
import {
    EXPENSE_CATEGORY_SEED,
    ExpenseCategory,
    expenseCategoriesRef,
    sortExpenseCategories
} from '@api/ExpenseCategories/ExpenseCategory';

// Lecturas: ~25 docs la PRIMERA vez por sesión; después, cero (caché en memoria,
// con dedupe de la carga en vuelo). La colección cambia solo cuando el admin
// general edita una categoría, y quien la edite llama a `forgetExpenseCategories`.
let cache: ExpenseCategory[] | undefined;
let inflight: Promise<ExpenseCategory[]> | undefined;

/**
 * Colección vacía = nunca sembrada: se escribe la semilla con sus ids fijos. Dos
 * pantallas que siembran a la vez escriben el mismo documento con lo mismo.
 */
const seedExpenseCategories = async (): Promise<ExpenseCategory[]> => {
    await Promise.all(EXPENSE_CATEGORY_SEED.map((category) => setDoc(doc(expenseCategoriesRef, category.id), category)));
    return EXPENSE_CATEGORY_SEED;
};

const loadExpenseCategories = async (): Promise<ExpenseCategory[]> => {
    const snapshot = await getDocs(expenseCategoriesRef);
    const categories = snapshot.empty
        ? await seedExpenseCategories()
        : snapshot.docs.map((document) => ({ ...(document.data() as ExpenseCategory), id: document.id }));
    return sortExpenseCategories(categories);
};

/** La bitácora de TODO el catálogo de categorías cuelga de un id fijo: la puerta es el botón "Actividad" del panel. */
export const EXPENSE_CATEGORIES_ACTIVITY = { section: 'expenseCategories', sectionId: 'all' };

const CATEGORY_FIELDS: FieldSpecs<ExpenseCategory> = {
    name: { label: 'el nombre', format: quoted },
    active: { label: 'el estado', verbs: { on: 'activó la categoría', off: 'desactivó la categoría' } },
    paidFromCashByDefault: {
        label: 'cómo arranca "salió de la caja"',
        verbs: { on: 'hizo que arranque como pagada de la caja', off: 'hizo que arranque como pagada por fuera de la caja' }
    }
};

export default {
    /** Todas, activas e inactivas, en el orden del Estado de Resultados. */
    async getExpenseCategories(): Promise<ExpenseCategory[]> {
        if (cache) return cache;
        if (!inflight) {
            inflight = loadExpenseCategories()
                .then((categories) => {
                    cache = categories;
                    return categories;
                })
                .finally(() => {
                    inflight = undefined;
                });
        }
        return inflight;
    },
    forgetExpenseCategories(): void {
        cache = undefined;
    },
    /**
     * Crear o editar una categoría (solo el admin general, §4.1). **No se borran**: se
     * desactivan, y cada gasto guarda copia de su nombre y su grupo. El grupo de una
     * categoría existente no cambia (movería lo histórico de renglón en el ER).
     */
    async saveExpenseCategory(category: ExpenseCategory, previous?: ExpenseCategory): Promise<void> {
        if (!canCurrentUser(Capability.ExpenseCategoriesManage)) {
            throw new Error('Solo el admin general administra las categorías');
        }
        const name = category.name.trim();
        if (!name) throw new Error('Escribe el nombre de la categoría');
        const toSave: ExpenseCategory = { ...category, name, group: previous?.group || category.group };
        await setDoc(doc(expenseCategoriesRef, toSave.id), toSave);
        this.forgetExpenseCategories();
        const text = previous
            ? describeChanges(previous, toSave, CATEGORY_FIELDS)
            : `creó la categoría "${name}"`;
        if (text) {
            await ActivityLogServ.addActivityLog(
                EXPENSE_CATEGORIES_ACTIVITY.section,
                EXPENSE_CATEGORIES_ACTIVITY.sectionId,
                previous ? `${text} ("${previous.name}")` : text
            );
        }
    },
    /** Un id nuevo generado en el cliente (sin ir a la red). */
    newExpenseCategoryId(): string {
        return doc(expenseCategoriesRef).id;
    }
};
